import {
    NotificationLevel,
    type CoreSignals,
    type CoreState,
    type InputFile,
    type ResourceForType,
    type TransportStateSnapshot,
    type PluginCommunication,
} from "@/client/core/Core.api";
import type {
    AddFilesMessageToPlatform,
    DeleteResourcesMessageToPlatform,
    GetAllResourcesMessageToPlatform,
    GetTransportStateMessageToPlatform,
    LabsInputFile,
    LabsResource,
    UpdateAssetMetadataMessageToPlatform,
} from "@/client/core/platform-types/labs.api";
import {
    LabsMessageToLiveType,
    LabsMessageToPlatformType,
    type LabsResourceType,
} from "@/client/core/platform-types/labs.api";
import { registerRequestAuthToken, setAuthToken } from "@/client/core/services/auth-token.signal";
import { createInputFile } from "@/client/core/services/resource-metadata";
import { createSendPluginMessage } from "@/client/core/services/plugin-send-message";
import { createErrorMessage } from "@/shared/utils/error";
import { setHostBottomInset } from "@/client/utils/hostInset";

interface Pending<T> {
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: unknown) => void;
}

export const createPluginCommunication = (
    namespace: string,
    state: CoreState,
    signals: CoreSignals,
): PluginCommunication => {
    const handleError = (title: string, error: unknown) => {
        signals.notification.notify({
            level: NotificationLevel.Error,
            title,
            body: createErrorMessage(error),
        });
    };

    const pendingResourcesResponses = new Map<number, Pending<LabsResource>>();
    const pendingDroppedAssetResponses = new Map<string, Pending<LabsResource>>();
    let pendingAllResourcesResponse: Pending<readonly LabsResource[]> | undefined;
    let pendingTransportStateResponse: Pending<TransportStateSnapshot> | undefined;

    const handleResource = (liveID: number, resource: LabsResource) => {
        const pending = pendingResourcesResponses.get(liveID);
        if (pending === undefined) {
            return;
        }
        pending.resolve(resource);
        pendingResourcesResponses.delete(liveID);
    };

    const handleDroppedAsset = (filename: string, resource: LabsResource) => {
        const pendingAsset = pendingDroppedAssetResponses.get(filename);
        if (pendingAsset !== undefined) {
            pendingAsset.resolve(resource);
            pendingDroppedAssetResponses.delete(filename);
        }
    };

    const sendMessage = createSendPluginMessage((message) => {
        switch (message.type) {
            case LabsMessageToLiveType.PortTransfer: {
                // noop, only used for setup
                break;
            }
            case LabsMessageToLiveType.AssetsDragStarted: {
                signals.assetsDragStarted.notify({
                    assets: message.assets,
                    transform: message.transform,
                });
                break;
            }
            case LabsMessageToLiveType.ResourceAdded: {
                const resource = message.resource;
                handleResource(message.liveID, resource);
                handleDroppedAsset(resource.file.name, resource);
                state.resources.set(resource.id.value, resource);
                signals.resources.notify(state.resources);
                break;
            }
            case LabsMessageToLiveType.AllResources: {
                const resources = message.resources;
                for (const resource of resources) {
                    handleDroppedAsset(resource.file.name, resource);
                }
                pendingAllResourcesResponse?.resolve(resources);
                pendingAllResourcesResponse = undefined;
                state.resources.clear();
                for (const resource of resources) {
                    state.resources.set(resource.id.value, resource);
                }
                signals.resources.notify(state.resources);
                break;
            }
            case LabsMessageToLiveType.Refresh: {
                window.location.reload();
                break;
            }
            case LabsMessageToLiveType.MidiEvent: {
                signals.midiNote.notify({
                    noteNumber: message.noteNumber,
                    velocity: message.velocity,
                });
                break;
            }
            case LabsMessageToLiveType.TransportState: {
                if (message.state.isPlaying !== state.transport.isPlaying) {
                    signals.transport.isPlaying.notify(message.state.isPlaying);
                }
                if (
                    message.state.timeSignature.numerator !==
                        state.transport.timeSignature.numerator ||
                    message.state.timeSignature.denominator !==
                        state.transport.timeSignature.denominator
                ) {
                    signals.transport.timeSignature.notify(message.state.timeSignature);
                }
                if (message.state.bpm !== state.transport.bpm) {
                    signals.transport.bpm.notify(message.state.bpm);
                }
                const position_beats =
                    message.state.position.unit === "beats"
                        ? message.state.position.value
                        : (message.state.position.value / 60) * message.state.bpm;
                if (position_beats !== state.transport.position_beats) {
                    signals.transport.position_beats.notify(position_beats);
                }
                if (pendingTransportStateResponse !== undefined) {
                    pendingTransportStateResponse.resolve({
                        ...state.transport,
                        position_beats,
                    });
                    pendingTransportStateResponse = undefined;
                }
                break;
            }
            case LabsMessageToLiveType.ElementaryEvent: {
                signals.meterEvents.notify(message.event);
                break;
            }
            case LabsMessageToLiveType.AuthToken: {
                setAuthToken(message.accessToken, message.expiresAt);
                break;
            }
            case LabsMessageToLiveType.ResourceExported: {
                // Sidepanel-host reply; never sent to the labs plugin's
                // iframe, where resources are host-owned from the start.
                break;
            }
            case LabsMessageToLiveType.HostBottomInset: {
                // The labs plugin has no floating footer today, but honor the
                // clearance if a host ever reports one.
                setHostBottomInset(message.px);
                break;
            }
        }
    }, handleError);

    registerRequestAuthToken(() => {
        sendMessage({ type: LabsMessageToPlatformType.RequestAuthToken });
    });

    let nextLiveID = 0;
    const addFiles: PluginCommunication["addFiles"] = async <T extends LabsResourceType>(
        inputFiles: InputFile<T>[],
    ) => {
        if (inputFiles.length === 0) {
            return [] as ResourceForType<T>[];
        }

        const arrayBuffers: ArrayBuffer[] = [];
        const files: LabsInputFile[] = [];
        const responsePromises: Promise<LabsResource>[] = [];

        for (const inputFile of inputFiles) {
            const arrayBuffer = await inputFile.file.arrayBuffer();
            const liveID = nextLiveID++;
            const promise = new Promise<LabsResource>((resolve, reject) => {
                pendingResourcesResponses.set(liveID, { resolve, reject });
            });
            promise.catch((error: unknown) => handleError("Add File", error));
            responsePromises.push(promise);

            arrayBuffers.push(arrayBuffer);
            files.push(createInputFile(inputFile, liveID));
        }

        sendMessage(
            {
                type: LabsMessageToPlatformType.AddFiles,
                namespace,
                files,
            } satisfies AddFilesMessageToPlatform,
            arrayBuffers,
        );

        const resources = await Promise.all(responsePromises);
        const inputTypes = new Set<LabsResourceType>(inputFiles.map((f) => f.type));
        return resources.filter((r): r is ResourceForType<T> => inputTypes.has(r.id.type));
    };

    const notifyFilesWereDropped: PluginCommunication["notifyFilesWereDropped"] = (files) => {
        if (files.length === 0) {
            return;
        }
    };

    const getAllResources: PluginCommunication["getAllResources"] = (): Promise<
        readonly LabsResource[]
    > => {
        return new Promise((resolve, reject) => {
            pendingAllResourcesResponse = { resolve, reject };
            sendMessage({
                type: LabsMessageToPlatformType.GetAllResources,
                namespace,
            } satisfies GetAllResourcesMessageToPlatform);
        });
    };

    const deleteResources: PluginCommunication["deleteResources"] = (ids) => {
        sendMessage({
            type: LabsMessageToPlatformType.DeleteResources,
            namespace,
            ids,
        } satisfies DeleteResourcesMessageToPlatform);
        return Promise.resolve();
    };

    const sendElementaryGraph: PluginCommunication["sendElementaryGraph"] = (graph) =>
        sendMessage({
            type: LabsMessageToPlatformType.ElementaryGraph,
            namespace,
            graph,
        });

    const revealFileOnDisk: PluginCommunication["revealResourceOnDisk"] = (id) =>
        sendMessage({
            type: LabsMessageToPlatformType.RevealResourceOnDisk,
            id,
        });

    const openExternalLink: PluginCommunication["openExternalLink"] = (url) =>
        sendMessage({
            type: LabsMessageToPlatformType.OpenExternalLink,
            url,
        });

    const updateResourceMetadata: PluginCommunication["updateResourceMetadata"] = (
        id,
        metadata,
    ) => {
        sendMessage({
            type: LabsMessageToPlatformType.UpdateResourceMetadata,
            namespace,
            id,
            metadata,
        } satisfies UpdateAssetMetadataMessageToPlatform);
        return Promise.resolve();
    };

    const getTransportState: PluginCommunication["getTransportState"] = () =>
        new Promise((resolve, reject) => {
            pendingTransportStateResponse = { resolve, reject };
            sendMessage({
                type: LabsMessageToPlatformType.GetTransportState,
            } satisfies GetTransportStateMessageToPlatform);
        });

    return {
        sendElementaryGraph,
        addFiles,
        notifyFilesWereDropped,
        revealResourceOnDisk: revealFileOnDisk,
        openExternalLink,
        getAllResources,
        deleteResources,
        updateResourceMetadata,
        getTransportState,
    };
};
